import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import axios from "axios";
import React, { useState } from "react";
import { GoAlert } from "react-icons/go";
import { RiVisaLine } from "react-icons/ri";
import { SiAmericanexpress, SiMastercard } from "react-icons/si";

import { triggerProgress } from "../../utilities/triggerProgress";
import Progressbar from "../Progressbar";

const PaymentCard = ({
  isDay,
  setIsOpen,
  due,
  isResumePayment,
  extraFunction,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [_error, set_Error] = useState(null);
  const [disabled, setDisabled] = React.useState(true);
  const [processing, setProcessing] = React.useState("");
  const [succeeded, setSucceeded] = React.useState(false);
  const [timeleft, setTimeleft] = React.useState(0);

  const { REACT_APP_STRIPE_PERCENTAGE } = process.env;
  const _price = +(due ?? 100);
  const _stripePer = +(REACT_APP_STRIPE_PERCENTAGE ?? 0.03);
  const price = +(_stripePer * _price + 0.3 + _price).toFixed(2); // adding stripe service charge

  React.useEffect(() => {
    if (succeeded) {
      triggerProgress(setTimeleft);
    }
  }, [succeeded]);

  const cardStyle = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: "Arial, sans-serif",
        fontSmoothing: "antialiased",
        fontSize: "12px",
        "::placeholder": {
          color: "#32325d",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
  };

  const handleChange = async (event) => {
    setDisabled(event.empty);
    set_Error(event.error ? event.error.message : "");
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setProcessing(true);

    let subTimout;
    clearTimeout(subTimout);

    try {
      const { data } = await axios.post(
        "/.netlify/functions/create-payment-intent",
        JSON.stringify({ price, isResumePayment })
      );

      if (data?.clientSecret) {
        const payload = await stripe.confirmCardPayment(data?.clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        });

        localStorage.setItem("payload", payload?.paymentIntent?.client_secret);

        if (payload.error) {
          set_Error(`Payment failed ${payload.error.message}`);
          setProcessing(false);
        } else {
          set_Error(null);
          setProcessing(false);
          setSucceeded(true);

          const calId = setTimeout(() => {
            setIsOpen(true);
            extraFunction();
          }, 10000);

          return () => clearTimeout(calId);
        }
      }
    } catch (error) {
      set_Error(error?.response?.data ? "Please contact wiingr Admin..." : "");
    }
  };

  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "CAD",
  }).format(price);

  return (
    <form
      id="payment-form"
      onSubmit={handleSubmit}
      className={`!pb-1 rounded-lg ease-in duration-300 w-full ${
        isDay ? "bg-neutral-800 text-white" : "bg-neutral-50 text-black"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex flex-row items-center">
          <div
            className={`${
              isDay ? "bg-white text-black" : "bg-black text-white"
            } px-1 rounded-full`}
          >
            <span className="text-[12px] font-semibold">{formatted}</span>
          </div>
          <div className="text-[10px] ml-[2px] text-green-700 font-semibold">
            <span>+Service charge</span>
          </div>
        </div>

        <div className="flex items-center justify-end mb-1">
          <SiMastercard size={20} className="mr-1 text-yellow-500" />
          <RiVisaLine size={30} className="mr-1 text-blue-900" />
          <div className="relative flex items-center mr-1">
            <RiVisaLine size={30} className="text-blue-800" />
            <span className="absolute bottom-[0px] right-[1px] text-[7px] italic">
              DEBIT
            </span>
          </div>
          <SiAmericanexpress size={20} className="mt-1 text-blue-600" />
        </div>
      </div>

      <CardElement
        id="card-element"
        options={cardStyle}
        onChange={handleChange}
        className={`w-full mx-auto border-[1px] p-2 rounded-[4px] ${
          isDay ? "border-green-900" : "border-green-600"
        }`}
      />
      <div className="flex justify-end w-full">
        <button
          className={`bg-black text-xs flex mt-2 px-2 py-1 rounded-md`}
          disabled={processing || disabled || succeeded}
          id="submit"
        >
          <span
            className={
              processing || disabled || succeeded
                ? "text-neutral-50 font-light"
                : "text-green-600 font-semibold"
            }
          >
            {processing ? (
              <div className="spinner" id="spinner"></div>
            ) : (
              "PAY NOW"
            )}
          </span>
        </button>
      </div>
      {_error && (
        <div
          className="flex items-center justify-center text-xs text-orange-700 card-error "
          role="alert"
        >
          <GoAlert className="mr-2" />
          {_error}
        </div>
      )}
      {succeeded && <Progressbar value={timeleft} max={100} />}
    </form>
  );
};

export { PaymentCard };
export default PaymentCard;
